import React, { useState, useEffect } from 'react';
import { X, User, Shield, ShoppingBag, Loader2, AlertCircle, CheckCircle, Building } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { adminApi } from '../../services/api';
import { 
  CreateMerchantUserRequest, 
  CreateSuperAdminUserRequest, 
  CreateUserResponse, 
  CreateUserType,
  TenantRecord 
} from '../../types';

interface CreateUserModalProps {
  onClose: () => void;
  onSuccess: (user: CreateUserResponse) => void;
}

type FormStep = 'type' | 'tenant' | 'details' | 'creating' | 'complete';

interface FormData {
  // User Type
  userType: CreateUserType;
  
  // Tenant (for merchant users)
  tenantId: string;
  tenantName: string;
  
  // User Details
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  company?: string;
  phone?: string;
  language: string;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState<FormStep>('type');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUser, setCreatedUser] = useState<CreateUserResponse | null>(null);
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    userType: 'merchant',
    tenantId: '',
    tenantName: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    language: 'en',
  });

  // Fetch tenants when selecting merchant user type
  useEffect(() => {
    if (formData.userType === 'merchant' && step === 'tenant' && tenants.length === 0) {
      fetchTenants();
    }
  }, [step, formData.userType]);

  const fetchTenants = async () => {
    try {
      setTenantsLoading(true);
      const data = await adminApi.getAllTenants();
      setTenants(data.filter(t => t.status === 'active'));
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
    } finally {
      setTenantsLoading(false);
    }
  };

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setError(null);
  };

  const validateDetails = (): boolean => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Invalid email format');
      return false;
    }
    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First and last name are required');
      return false;
    }
    return true;
  };

  const validateTenant = (): boolean => {
    if (formData.userType === 'merchant' && !formData.tenantId) {
      setError('Please select a tenant');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError(null);
    if (step === 'type') {
      // Skip tenant selection for super_admin
      if (formData.userType === 'super_admin') {
        setStep('details');
      } else {
        setStep('tenant');
      }
    } else if (step === 'tenant' && validateTenant()) {
      setStep('details');
    }
  };

  const handleBack = () => {
    setError(null);
    if (step === 'details') {
      if (formData.userType === 'super_admin') {
        setStep('type');
      } else {
        setStep('tenant');
      }
    } else if (step === 'tenant') {
      setStep('type');
    }
  };

  const handleCreate = async () => {
    if (!validateDetails()) return;

    try {
      setLoading(true);
      setError(null);
      setStep('creating');

      let result: CreateUserResponse;

      if (formData.userType === 'super_admin') {
        const request: CreateSuperAdminUserRequest = {
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
        };
        result = await adminApi.createSuperAdminUser(request);
      } else {
        const request: CreateMerchantUserRequest = {
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          company: formData.company?.trim() || undefined,
          phone: formData.phone?.trim() || undefined,
          language: formData.language,
        };
        result = await adminApi.createMerchantUser(formData.tenantId, request);
      }

      setCreatedUser(result);
      setStep('complete');

      // Auto-close and trigger success after 3 seconds
      setTimeout(() => {
        onSuccess(result);
      }, 3000);
    } catch (err: any) {
      setError(err.userMessage || err.message || 'Failed to create user');
      setStep('details'); // Go back to details step
    } finally {
      setLoading(false);
    }
  };

  const getSteps = () => {
    if (formData.userType === 'super_admin') {
      return ['type', 'details'] as const;
    }
    return ['type', 'tenant', 'details'] as const;
  };

  const getCurrentStepIndex = () => {
    const steps = getSteps();
    return steps.indexOf(step as any);
  };

  return (
    <Modal isOpen onClose={onClose} title="">
      <div className="p-6">
        {/* Progress Indicator */}
        {step !== 'creating' && step !== 'complete' && (
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {getSteps().map((s, idx) => (
                <React.Fragment key={s}>
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step === s
                        ? 'bg-primary text-white'
                        : getCurrentStepIndex() > idx
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {idx + 1}
                    </div>
                    <span className="ml-2 text-sm font-medium dark:text-gray-300 text-gray-700 capitalize">
                      {s === 'type' ? 'User Type' : s === 'tenant' ? 'Select Tenant' : 'Details'}
                    </span>
                  </div>
                  {idx < getSteps().length - 1 && (
                    <div className={`flex-1 h-1 mx-3 rounded ${
                      getCurrentStepIndex() > idx
                        ? 'bg-green-500'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* User Type Selection Step */}
        {step === 'type' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Create New User
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Step 1: Select user type
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => updateField('userType', 'merchant')}
                className={`w-full p-4 rounded-lg border-2 transition-all ${
                  formData.userType === 'merchant'
                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${
                    formData.userType === 'merchant'
                      ? 'bg-green-100 dark:bg-green-900/30'
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <ShoppingBag className={`w-6 h-6 ${
                      formData.userType === 'merchant'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-500'
                    }`} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold dark:text-white text-gray-900">
                      Merchant User
                    </h3>
                    <p className="text-sm dark:text-gray-400 text-gray-600">
                      Business account with access to tenant dashboard and resources
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => updateField('userType', 'super_admin')}
                className={`w-full p-4 rounded-lg border-2 transition-all ${
                  formData.userType === 'super_admin'
                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${
                    formData.userType === 'super_admin'
                      ? 'bg-purple-100 dark:bg-purple-900/30'
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <Shield className={`w-6 h-6 ${
                      formData.userType === 'super_admin'
                        ? 'text-purple-600 dark:text-purple-400'
                        : 'text-gray-500'
                    }`} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold dark:text-white text-gray-900">
                      Super Admin
                    </h3>
                    <p className="text-sm dark:text-gray-400 text-gray-600">
                      Full system access across all tenants (uses global login)
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleNext} className="flex-1">
                Next
              </Button>
            </div>
          </>
        )}

        {/* Tenant Selection Step (for merchants only) */}
        {step === 'tenant' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Select Tenant
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Step 2: Choose the tenant for this merchant user
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-2">
                Tenant <span className="text-red-500">*</span>
              </label>
              
              {tenantsLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2 text-sm dark:text-gray-400 text-gray-600">Loading tenants...</span>
                </div>
              ) : (
                <select
                  value={formData.tenantId}
                  onChange={(e) => {
                    const tenant = tenants.find(t => t.tenantId === e.target.value);
                    updateField('tenantId', e.target.value);
                    updateField('tenantName', tenant?.name || '');
                  }}
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select a tenant...</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.name} ({tenant.tenantId})
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-2 text-xs dark:text-gray-500 text-gray-600">
                The merchant user will be able to login using global login after creation
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleBack} className="flex-1">
                Back
              </Button>
              <Button variant="primary" onClick={handleNext} className="flex-1" disabled={!formData.tenantId}>
                Next
              </Button>
            </div>
          </>
        )}

        {/* User Details Step */}
        {step === 'details' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                {formData.userType === 'super_admin' ? (
                  <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                ) : (
                  <ShoppingBag className="w-6 h-6 text-green-600 dark:text-green-400" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  User Details
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  {formData.userType === 'super_admin' 
                    ? 'Step 2: Enter super admin details'
                    : `Step 3: Enter merchant details for ${formData.tenantName}`
                  }
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    placeholder="John"
                    className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    placeholder="Doe"
                    className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateField('confirmPassword', e.target.value)}
                  placeholder="Re-enter password"
                  className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              {formData.userType === 'merchant' && (
                <>
                  <div>
                    <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => updateField('company', e.target.value)}
                      placeholder="Company name (optional)"
                      className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="+1234567890 (optional)"
                      className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium dark:text-gray-300 text-gray-700 mb-1">
                      Language
                    </label>
                    <select
                      value={formData.language}
                      onChange={(e) => updateField('language', e.target.value)}
                      className="w-full px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="el">Greek</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 mb-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Note:</strong> {formData.userType === 'super_admin' 
                  ? 'Super admin users can login using global login and have access to all system features.'
                  : 'Merchant users can login using global login to access their tenant dashboard.'
                }
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleBack} className="flex-1">
                Back
              </Button>
              <Button variant="primary" onClick={handleCreate} className="flex-1" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </Button>
            </div>
          </>
        )}

        {/* Creating Progress */}
        {step === 'creating' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  Creating User...
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  Please wait while we set up the account
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {[
                'Validating user information',
                'Creating user account',
                'Setting up authentication',
                'Registering in user directory',
                'Finalizing setup'
              ].map((stepName, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border dark:border-gray-700 border-gray-200">
                  <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                  <span className="text-sm dark:text-gray-300 text-gray-700">{stepName}...</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Complete Step */}
        {step === 'complete' && createdUser && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  User Created Successfully!
                </h2>
                <p className="text-sm dark:text-gray-400 text-gray-600">
                  {formData.firstName} {formData.lastName} is ready to login
                </p>
              </div>
            </div>

            <div className="p-4 rounded-lg border dark:border-gray-700 border-gray-200 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs dark:text-gray-500 text-gray-600 uppercase tracking-wide">
                    Name
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 mt-1">
                    {createdUser.personalInfo.firstName} {createdUser.personalInfo.lastName}
                  </p>
                </div>
                <div>
                  <label className="text-xs dark:text-gray-500 text-gray-600 uppercase tracking-wide">
                    Email
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 mt-1">
                    {createdUser.personalInfo.email}
                  </p>
                </div>
                <div>
                  <label className="text-xs dark:text-gray-500 text-gray-600 uppercase tracking-wide">
                    User Type
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 mt-1 capitalize">
                    {createdUser.type.replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <label className="text-xs dark:text-gray-500 text-gray-600 uppercase tracking-wide">
                    User ID
                  </label>
                  <p className="text-sm dark:text-white text-gray-900 font-mono mt-1 truncate">
                    {createdUser.userId}
                  </p>
                </div>
                {formData.userType === 'merchant' && (
                  <div className="col-span-2">
                    <label className="text-xs dark:text-gray-500 text-gray-600 uppercase tracking-wide">
                      Tenant
                    </label>
                    <p className="text-sm dark:text-white text-gray-900 mt-1">
                      {formData.tenantName} ({formData.tenantId})
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 mb-4">
              <p className="text-sm text-green-700 dark:text-green-300">
                <strong>✓ Global Login Enabled:</strong> This user can now login using global authentication at the main login page.
              </p>
            </div>

            <p className="text-sm text-center dark:text-gray-400 text-gray-600 mb-4">
              Closing automatically in 3 seconds...
            </p>

            <Button onClick={() => onSuccess(createdUser)} className="w-full">
              Done
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CreateUserModal;
